import React from 'react';
import { MapPin, Navigation, X, Car, Calendar, Ruler, Clock } from 'lucide-react';
import { MobilityParkingLocation } from '../types';

interface MobilityParkingPopupProps {
  location: MobilityParkingLocation;
  onClose: () => void;
}

const MobilityParkingPopup: React.FC<MobilityParkingPopupProps> = ({ location, onClose }) => {
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className="mobility-parking-modal-backdrop" onClick={handleBackdropClick}>
      <div className="mobility-parking-modal">
        {/* Modal Header */}
        <div className="modal-header">
          <h3 className="modal-title">
            <Car size={20} />
            Mobility Parking
          </h3>
          <button onClick={onClose} className="modal-close-button">
            <X size={20} />
          </button>
        </div>

        {/* Modal Content */}
        <div className="modal-content">
          <div className="location-details">
            <div className="detail-row">
              <span className="detail-label">Street:</span>
              <span className="detail-value">{location.street}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Address:</span>
              <span className="detail-value">{location.address || 'N/A'}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Suburb:</span>
              <span className="detail-value">{location.suburb}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Location:</span>
              <span className="detail-value">{location.location}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Side of Street:</span>
              <span className="detail-value">{location.sideOfStreet}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Coordinates:</span>
              <span className="detail-value">
                {location.latitude.toFixed(6)}, {location.longitude.toFixed(6)}
              </span>
            </div>
          </div>

          {/* Parking Space Details */}
          <div className="parking-details">
            <h4 className="section-title">Parking Space Details</h4>
            <div className="detail-row">
              <span className="detail-label">
                <Car size={16} />
                Spaces:
              </span>
              <span className="detail-value">{location.numberParkingSpaces}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">
                <Ruler size={16} />
                Width:
              </span>
              <span className="detail-value">{location.parkingSpaceWidth}m</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">
                <Ruler size={16} />
                Length:
              </span>
              <span className="detail-value">{location.parkingSpaceLength}m</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Angle:</span>
              <span className="detail-value">{location.parkingSpaceAngle}</span>
            </div>
          </div>

          {/* Additional Information */}
          {location.signText && (
            <div className="sign-details">
              <h4 className="section-title">Sign Information</h4>
              <div className="sign-text">{location.signText}</div>
            </div>
          )}

          {/* Audit Information */}
          <div className="audit-details">
            <div className="detail-row">
              <span className="detail-label">
                <Calendar size={16} />
                Audit Date:
              </span>
              <span className="detail-value">{location.auditDate}</span>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="modal-actions">
            <a
              href={`https://www.google.com/maps/dir/?api=1&destination=${location.latitude},${location.longitude}`}
              target="_blank"
              rel="noopener noreferrer"
              className="directions-button"
            >
              <Navigation size={16} />
              Get Directions
            </a>
            <a
              href={`https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${location.latitude},${location.longitude}`}
              target="_blank"
              rel="noopener noreferrer"
              className="streetview-button"
            >
              <MapPin size={16} />
              Street View
            </a>
            {location.url && (
              <a href={location.url} target="_blank" rel="noopener noreferrer" className="info-button">
                <Clock size={16} />
                More Info
              </a>
            )}
          </div>

          {/* Powered by City of Sydney Card */}
          <div className="powered-by-sydney-card">
            <div className="powered-by-content">
              <div className="powered-by-text">Powered by</div>
              <div className="city-of-sydney-logo">
                <img
                  src="/instantAppsConfigPanel_1712885390298_Anchor_White.png"
                  alt="City of Sydney Logo"
                  className="city-logo-image"
                />
                <span className="city-name">City of Sydney</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MobilityParkingPopup;
